import { useState } from 'react'
import { PickList, TextField } from '@bloo-team/ui'
import { OUTLIER_FUNCTIONS, OUTLIER_TWO_OPERAND, readOutlierSpec, writeOutlierSpec } from '@bloo-team/api-client'
import type { OutlierFeature, OutlierSpec } from '@bloo-team/api-client'
import { useOutlierRecommendations } from '../../../lib/workbook-run'
import { InlineKv, Section } from './parts'
import type { BlockEditorProps, BlockViewProps } from './types'

/**
 * Outlier (`ai`): anomaly detection over one stream, per entity, from a
 * feature list. "Suggest features" asks outlier/recommendations for the stream
 * (captured 2026-09-28) and offers each entity's feature set.
 */

const featureLabel = (f: OutlierFeature) => `${f.fn}(${f.value !== null ? `${f.field}, ${f.value}` : f.field})`
const OPERATORS = ['=', '!=', '>', '<', '>=', '<=']

export function OutlierView({ block }: BlockViewProps) {
  const s = readOutlierSpec(block)
  const window = s.duration ?? `${s.startTime ?? '—'} → ${s.endTime ?? '—'}`
  const result: Array<[string, boolean]> = [
    ['only anomalies', s.onlyAnomaly],
    ['graph result', s.graphResult],
    ['feature contribution', s.featureContribution],
  ]
  return (
    <div className="wb-secs">
      <Section label="Scope">
        <InlineKv items={[['stream', s.stream], ['entity', s.entity], ['window', window], ['contamination', String(s.contamination)]]} />
      </Section>
      <Section label="Features" count={s.features.length}>
        {s.features.length ? (
          <div className="wb-chips">
            {s.features.map((f, i) => <span key={i} className="bui-chip bui-chip-solid mono">{featureLabel(f)}</span>)}
          </div>
        ) : (
          <p className="wb-empty">none</p>
        )}
      </Section>
      <Section label="Filters" count={s.filters.length}>
        {s.filters.length ? (
          <div className="wb-chips">
            {s.filters.map((f, i) => <span key={i} className="bui-chip mono">{`${f.field} ${f.operator} ${f.value}`}</span>)}
          </div>
        ) : (
          <p className="wb-empty">none</p>
        )}
      </Section>
      <Section label="Result">
        <div className="wb-chips">
          {result.map(([label, on]) => (
            <span key={label} className={on ? 'bui-chip bui-chip-accent' : 'bui-chip'}>{on ? '✓' : '—'} {label}</span>
          ))}
        </div>
      </Section>
    </div>
  )
}

export function OutlierEditor({ block, clusterId, scopeId, index, onChange }: BlockEditorProps) {
  const s = readOutlierSpec(block)
  const set = (patch: Partial<OutlierSpec>) => onChange(writeOutlierSpec(block, { ...s, ...patch }))
  const [suggest, setSuggest] = useState(false)
  const recs = useOutlierRecommendations({ clusterId, scopeId, stream: s.stream, enabled: suggest })

  const setFeature = (i: number, patch: Partial<OutlierFeature>) =>
    set({
      features: s.features.map((f, j) => {
        if (j !== i) return f
        const next = { ...f, ...patch }
        return { ...next, value: OUTLIER_TWO_OPERAND.has(next.fn) ? (next.value ?? '') : null }
      }),
    })

  return (
    <div className="wb-secs">
      <Section label="Scope">
        <div className="wb-fields">
          <TextField className="wb-w-md" label="stream" value={s.stream} onChange={(v) => set({ stream: v })} />
          <TextField className="wb-w-md" label="entity" placeholder="$SrcIP" value={s.entity} onChange={(v) => set({ entity: v })} />
          {s.duration !== null ? (
            <TextField className="wb-w-sm" label="duration" hint="e.g. 1h · 1d · 1M" value={s.duration} onChange={(v) => set({ duration: v })} />
          ) : (
            <p className="hud-unit mono">window {s.startTime} → {s.endTime}</p>
          )}
          <TextField
            className="wb-w-xs"
            label="contamination"
            value={String(s.contamination)}
            onChange={(v) => {
              const n = Number(v)
              if (Number.isFinite(n)) set({ contamination: n })
            }}
          />
        </div>
      </Section>

      <Section label="Features" count={s.features.length}>
        <div>
          <button
            type="button"
            className="list-chip"
            aria-pressed={suggest}
            disabled={!s.stream.trim()}
            onClick={() => setSuggest((v) => !v)}
          >
            suggest features
          </button>
        </div>
        {suggest ? (
          recs.isPending ? (
            <p className="hud-unit" role="status">Asking for suggestions…</p>
          ) : recs.isError ? (
            <p className="hud-unit">Suggestions could not be loaded.</p>
          ) : (recs.data ?? []).length === 0 ? (
            <p className="hud-unit">No suggestions for {s.stream}.</p>
          ) : (
            <div className="hud-stack wb-gap-1" aria-label="Suggested feature sets">
              {(recs.data ?? []).map((r) => (
                <div key={r.entity} className="hud-row wb-gap-1 wb-wrap">
                  <button
                    type="button"
                    className="bui-btn bui-btn-outline bui-btn-sm"
                    onClick={() => {
                      set({ entity: r.entity, features: r.features })
                      setSuggest(false)
                    }}
                  >
                    use for {r.entity}
                  </button>
                  {r.features.map((f, i) => <span key={i} className="bui-chip mono">{featureLabel(f)}</span>)}
                </div>
              ))}
            </div>
          )
        ) : null}
        {/* One row per feature: function, field[, value], ✕ — the Signal
            entity rows' pattern, so heights stay even and the list scans. */}
        <div className="hud-stack wb-gap-1">
          {s.features.map((f, i) => (
            <div key={i} className="wb-ent-row" role="group" aria-label={`Feature ${i + 1}`}>
              <span className="wb-fn">
                <PickList
                  label={`Feature ${i + 1} function`}
                  options={[...OUTLIER_FUNCTIONS]}
                  selected={[f.fn]}
                  onChange={(next) => setFeature(i, { fn: next[0] ?? f.fn })}
                />
              </span>
              <TextField
                className="wb-w-md"
                aria-label={`Feature ${i + 1} field`}
                placeholder="field"
                value={f.field}
                onChange={(v) => setFeature(i, { field: v })}
              />
              {OUTLIER_TWO_OPERAND.has(f.fn) ? (
                <TextField
                  className="wb-w-md"
                  aria-label={`Feature ${i + 1} value`}
                  placeholder="value"
                  value={f.value ?? ''}
                  onChange={(v) => setFeature(i, { value: v })}
                />
              ) : null}
              <button
                type="button"
                className="row-action"
                aria-label={`Remove feature ${i + 1}`}
                onClick={() => set({ features: s.features.filter((_, j) => j !== i) })}
              >
                ✕
              </button>
            </div>
          ))}
          <div>
            <button
              type="button"
              className="list-chip"
              onClick={() => set({ features: [...s.features, { fn: 'distinct', field: '', value: null }] })}
            >
              + feature
            </button>
          </div>
        </div>
      </Section>

      <Section label="Filters" count={s.filters.length}>
        <div className="hud-stack wb-gap-1">
          {s.filters.length === 0 ? <p className="wb-empty">none</p> : null}
          {s.filters.map((f, i) => (
            <div key={i} className="wb-ent-row" role="group" aria-label={`Filter ${i + 1}`}>
              <TextField
                className="wb-w-md"
                aria-label={`Filter ${i + 1} field`}
                placeholder="field"
                value={f.field}
                onChange={(v) => set({ filters: s.filters.map((x, j) => (j === i ? { ...x, field: v } : x)) })}
              />
              <PickList
                label={`Filter ${i + 1} operator`}
                options={OPERATORS.includes(f.operator) ? OPERATORS : [f.operator, ...OPERATORS]}
                selected={[f.operator]}
                onChange={(next) =>
                  set({ filters: s.filters.map((x, j) => (j === i ? { ...x, operator: next[0] ?? x.operator } : x)) })
                }
              />
              <TextField
                className="wb-w-md"
                aria-label={`Filter ${i + 1} value`}
                placeholder="value"
                value={f.value}
                onChange={(v) => set({ filters: s.filters.map((x, j) => (j === i ? { ...x, value: v } : x)) })}
              />
              <button
                type="button"
                className="row-action"
                aria-label={`Remove filter ${i + 1}`}
                onClick={() => set({ filters: s.filters.filter((_, j) => j !== i) })}
              >
                ✕
              </button>
            </div>
          ))}
          <div>
            <button
              type="button"
              className="list-chip"
              onClick={() => set({ filters: [...s.filters, { field: '', operator: '=', value: '' }] })}
            >
              + filter
            </button>
          </div>
        </div>
      </Section>

      <Section label="Result">
        <div className="hud-row wb-gap-3 wb-wrap" aria-label={`Block ${index + 1} result conditions`}>
          <label className="bui-check">
            <input type="checkbox" checked={s.onlyAnomaly} onChange={(e) => set({ onlyAnomaly: e.target.checked })} />
            <span>only anomalies</span>
          </label>
          <label className="bui-check">
            <input type="checkbox" checked={s.graphResult} onChange={(e) => set({ graphResult: e.target.checked })} />
            <span>graph result</span>
          </label>
          <label className="bui-check">
            <input
              type="checkbox"
              checked={s.featureContribution}
              onChange={(e) => set({ featureContribution: e.target.checked })}
            />
            <span>feature contribution</span>
          </label>
        </div>
      </Section>
    </div>
  )
}
